import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Warning, Sparkle } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The NPD note, with the AI check the user asked for (Oct 17 2026):
//   "the NBD members' fill must be LLM-checked. It should reasonably explain
//    why they need it and what they are making (basics). The note suggester
//    should advise what it exactly wants."
//
// The verdict comes from the backend (npd_note_check_service -> Jev), which
// also re-checks on submit; this is the live coach so nobody discovers the
// problem only after pressing the button. It never blocks typing - it tells
// you exactly which of the two basics is missing.
export const NpdNoteField = ({
  value, onChange, projectId, lines = [], onVerdict,
  label = "Remark *", testIdPrefix = "npd-remark",
}) => {
  const [verdict, setVerdict] = useState(null);
  const [checking, setChecking] = useState(false);
  const timer = useRef(null);

  const check = useCallback(async (note, project, items) => {
    if (!note || note.trim().length < 5) {
      setVerdict(null);
      onVerdict?.(null);
      return;
    }
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests/note-check`, {
        note, project_id: project || null, lines: items,
      });
      setVerdict(data);
      onVerdict?.(data);
    } catch {
      setVerdict(null);
      onVerdict?.(null);
    } finally {
      setChecking(false);
    }
  }, [onVerdict]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handle = (next) => {
    onChange(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => check(next, projectId, lines), 900);
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs font-bold text-[#344054]">{label}</Label>
      <Textarea
        value={value}
        onChange={(e) => handle(e.target.value)}
        onBlur={() => check(value, projectId, lines)}
        placeholder="Why do you need this material? A general reason is fine - trial, sample, testing, rework"
        rows={2}
        className="text-[13px]"
        data-testid={`${testIdPrefix}-input`}
      />
      <p className="text-[10px] text-[#667085] flex items-start gap-1" data-testid={`${testIdPrefix}-hint`}>
        <Sparkle size={11} weight="fill" className="mt-0.5 shrink-0 text-[#0E7C86]" />
        <span>
          A general reason is enough - trial, sample, prototype, testing, rework or machine setup.
          No part number or quantity needed.
          {verdict?.example ? <span className="block text-[#98A2B3]">{verdict.example}</span> : null}
        </span>
      </p>
      {checking && <p className="text-[10px] text-[#667085]" data-testid={`${testIdPrefix}-checking`}>Checking the note...</p>}
      {!checking && verdict?.status === "checked" && verdict.ok && (
        <p className="text-[11px] text-[#027A48] flex items-center gap-1" data-testid={`${testIdPrefix}-ok`}>
          <CheckCircle size={12} weight="fill" /> Reads clearly - the reason comes across.
        </p>
      )}
      {!checking && verdict?.status === "checked" && !verdict.ok && (
        <div className="text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1.5 space-y-0.5" data-testid={`${testIdPrefix}-guidance`}>
          <p className="flex items-center gap-1 font-bold"><Warning size={12} weight="fill" /> This note still needs:</p>
          {verdict.guidance.map((g) => <p key={g}>• {g}</p>)}
        </div>
      )}
      {!checking && verdict?.status === "unavailable" && (
        <p className="text-[11px] text-[#667085]" data-testid={`${testIdPrefix}-unavailable`}>
          The note check could not run just now - your note will be saved unchecked.
        </p>
      )}
    </div>
  );
};
